/**
 * LoginForm Component
 * คอมโพเนนต์ฟอร์มเข้าสู่ระบบ
 *
 * Uses react-hook-form + zod for validation.
 * ใช้ react-hook-form + zod สำหรับการตรวจสอบ
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, Loader2, Mail, Lock } from 'lucide-react'

// Validation schema
const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
})

type LoginFormValues = z.infer<typeof loginSchema>

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void>
  isLoading: boolean
  error: string | null
}

export function LoginForm({ onSubmit, isLoading, error }: LoginFormProps) {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  })

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e)
      }}
      className="space-y-5"
      noValidate
    >
      {/* Error Alert */}
      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-[11px] font-medium text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400"
          role="alert"
          id="login-error"
        >
          {error}
        </div>
      )}

      {/* Email Field */}
      <div className="space-y-2">
        <label
          htmlFor="login-email"
          className="text-foreground/70 text-[10px] font-bold tracking-widest uppercase"
        >
          {t('auth.email')}
        </label>
        <div className="relative">
          <Mail className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder={t('auth.emailPlaceholder')}
            className={`bg-background placeholder:text-muted-foreground focus:ring-primary/30 h-9 w-full rounded-lg border pr-4 pl-9 text-xs transition-colors focus:ring-2 focus:outline-none ${
              errors.email
                ? 'border-red-500 focus:ring-red-500/30'
                : 'border-input hover:border-primary/50'
            }`}
            {...register('email')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
        </div>
        {errors.email && (
          <p id="email-error" className="text-[10px] font-medium text-red-500">
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div className="space-y-2">
        <label
          htmlFor="login-password"
          className="text-foreground/70 text-[10px] font-bold tracking-widest uppercase"
        >
          {t('auth.password')}
        </label>
        <div className="relative">
          <Lock className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder={t('auth.passwordPlaceholder')}
            className={`bg-background placeholder:text-muted-foreground focus:ring-primary/30 h-9 w-full rounded-lg border pr-10 pl-9 text-xs transition-colors focus:ring-2 focus:outline-none ${
              errors.password
                ? 'border-red-500 focus:ring-red-500/30'
                : 'border-input hover:border-primary/50'
            }`}
            {...register('password')}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          <button
            type="button"
            onClick={() => {
              setShowPassword(!showPassword)
            }}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        {errors.password && (
          <p id="password-error" className="text-[10px] font-medium text-red-500">
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember Me */}
      <div className="flex items-center gap-2">
        <input
          id="remember-me"
          type="checkbox"
          className="border-input text-primary accent-primary h-4 w-4 rounded"
          {...register('rememberMe')}
        />
        <label htmlFor="remember-me" className="text-muted-foreground text-xs">
          {t('auth.rememberMe')}
        </label>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="h-10 w-full text-xs font-bold tracking-widest uppercase"
        disabled={isLoading}
        id="login-submit"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          t('auth.signIn')
        )}
      </Button>
    </form>
  )
}
